import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icon/Icon';
import styles from './SideNav.module.css';

/**
 * Shared filter / tab rail. Same visual language as the main app Sidebar
 * (icon + label rows, accent-tint when active, paper-2 hover) so a page's
 * left-hand filter pane looks like a continuation of the chrome instead of
 * a Card-on-Card surface.
 *
 * The pieces are composable rather than declarative so callers can mix
 * filter items, eyebrow sections, and free-form chip rows (see Documents
 * tag chips). Tabs use `role="tab"` semantics; plain filters render as
 * `<button>` so the screen-reader role doesn't lie.
 */

export function SideNav({
  children,
  ariaLabel,
}: {
  children: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <nav className={styles.nav} aria-label={ariaLabel}>
      {children}
    </nav>
  );
}

export function SideNavSection({ children }: { children: ReactNode }) {
  return <div className={styles.section}>{children}</div>;
}

export interface SideNavItemProps {
  label: string;
  icon?: IconName;
  active?: boolean;
  count?: number;
  onClick?: () => void;
  /**
   * When set to "tab", the button advertises tab semantics so Settings can
   * keep its `role="tab"` / `aria-selected` contract intact.
   */
  role?: 'tab';
  /**
   * Used by `role="tab"` consumers; ignored otherwise.
   */
  ariaSelected?: boolean;
}

export function SideNavItem({
  label,
  icon,
  active,
  count,
  onClick,
  role,
  ariaSelected,
}: SideNavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.item} ${active ? styles.active : ''}`}
      {...(role === 'tab' ? { role: 'tab', 'aria-selected': ariaSelected ?? active ?? false } : {})}
    >
      {icon && (
        <span className={styles.iconWrap}>
          <Icon name={icon} size={15} />
        </span>
      )}
      <span className={styles.label}>{label}</span>
      {count !== undefined && <span className={styles.count}>{count}</span>}
    </button>
  );
}

/**
 * Free-form row inside the nav (used by Documents for the tag chip cloud).
 * Renders as a flex-wrap container; callers compose chip buttons + Pills.
 */
export function SideNavChips({ children }: { children: ReactNode }) {
  return <div className={styles.chips}>{children}</div>;
}

/**
 * Single chip button. Wraps a Pill so the visual matches existing usage but
 * adds focus-visible + reset button styling.
 */
export function SideNavChip({
  children,
  active,
  onClick,
  ariaLabel,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.chipBtn}
      aria-pressed={active ?? false}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}
